// Standalone public chart build: talks ONLY to the /api/public/* namespace,
// via plain fetch — no axios (keeps the standalone bundle free of axios's
// own internal browser-origin fallback string, which otherwise trips the
// "no localhost/internal URLs in the bundle" build check even though it
// isn't a real leak).
//
// Every /api/public/* route except /api/public/auth/* requires the chart
// JWT (see backend/app/api/dependencies_chart.py) — attached here from
// chartAuthStore. On 401 the stored session is cleared, which makes
// ChartAuthGate re-render into the login view (no page reload needed).
//
// Auto-login on 401: the chart JWT has no refresh token. If we get a 401
// and the user has a remembered password in sessionStorage, we try one
// silent login and retry the original request. If that fails too, we
// logout and let ChartAuthGate show the login form.
import useChartAuthStore from '../store/chartAuthStore'
import * as publicAuthApi from './publicAuthApi'
import { log as debugLog } from '@/utils/logger'

const BASE_URL = import.meta.env.VITE_API_BASE || '/api/public'

function authHeaders() {
  const token = useChartAuthStore.getState().token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function handleUnauthorized(res) {
  if (res.status === 401) useChartAuthStore.getState().logout()
}

async function get(path, { params, signal, timeout } = {}) {
  const url = new URL(BASE_URL + path, window.location.origin)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, v)
    }
  }
  const controller = timeout ? new AbortController() : null
  let timer = null
  if (controller) {
    timer = setTimeout(() => controller.abort(), timeout)
  }
  try {
    const res = await fetch(url.pathname + url.search, {
      headers: authHeaders(),
      signal: signal || controller?.signal,
    })
    if (res.status === 401) {
      const retried = await trySilentLoginAndRetry(() =>
        fetch(url.pathname + url.search, { headers: authHeaders(), signal: signal || controller?.signal })
      )
      if (retried) return parseResponse(retried)
    }
    handleUnauthorized(res)
    return parseResponse(res)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// Igual que get(), pero para respuestas binarias (p.ej. el PNG del análisis
// diario, que va detrás del JWT y no se puede cargar con <img src>):
// devuelve el Blob crudo. Mismo auth, mismo auto-login en 401.
async function getBlob(path, { params, signal } = {}) {
  const url = new URL(BASE_URL + path, window.location.origin)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, v)
    }
  }
  const doFetch = () => fetch(url.pathname + url.search, { headers: authHeaders(), signal })
  let res = await doFetch()
  if (res.status === 401) {
    const retried = await trySilentLoginAndRetry(doFetch)
    if (retried) res = retried
  }
  handleUnauthorized(res)
  if (!res.ok) {
    const err = new Error(`Request failed: ${res.status}`)
    err.status = res.status
    try { err.body = await res.json() } catch { /* ignore */ }
    throw err
  }
  return res.blob()
}

async function post(path, body) {
  const res = await fetch(BASE_URL + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    const retried = await trySilentLoginAndRetry(() =>
      fetch(BASE_URL + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      })
    )
    if (retried) return parseResponse(retried)
  }
  handleUnauthorized(res)
  return parseResponse(res)
}

async function patch(path, body) {
  const res = await fetch(BASE_URL + path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    const retried = await trySilentLoginAndRetry(() =>
      fetch(BASE_URL + path, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      })
    )
    if (retried) return parseResponse(retried)
  }
  handleUnauthorized(res)
  return parseResponse(res)
}

async function put(path, body) {
  const res = await fetch(BASE_URL + path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    const retried = await trySilentLoginAndRetry(() =>
      fetch(BASE_URL + path, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      })
    )
    if (retried) return parseResponse(retried)
  }
  handleUnauthorized(res)
  return parseResponse(res)
}

// DELETE sin body. 204 No Content: parseResponse haría res.json() sobre un
// body vacío y fallaría — aquí se devuelve { data: null } directamente.
async function del(path) {
  const doFetch = () => fetch(BASE_URL + path, { method: 'DELETE', headers: authHeaders() })
  let res = await doFetch()
  if (res.status === 401) {
    const retried = await trySilentLoginAndRetry(doFetch)
    if (retried) res = retried
  }
  handleUnauthorized(res)
  if (!res.ok) {
    const err = new Error(`Request failed: ${res.status}`)
    err.status = res.status
    try { err.body = await res.json() } catch { /* ignore */ }
    throw err
  }
  return { data: null }
}

async function parseResponse(res) {
  if (!res.ok) {
    const err = new Error(`Request failed: ${res.status}`)
    err.status = res.status
    try { err.body = await res.json() } catch { /* ignore */ }
    throw err
  }
  return { data: await res.json() }
}

async function trySilentLoginAndRetry(retryFn) {
  const state = useChartAuthStore.getState()
  const { username, password } = state
  if (!username || !password) return null
  try {
    // eslint-disable-next-line no-console
    debugLog('[publicApi] 401 detected, attempting silent login for:', username)
    const data = await publicAuthApi.login(username, password)
    useChartAuthStore.getState().setSession(data.access_token, data.username, password)
    const retried = await retryFn()
    // eslint-disable-next-line no-console
    debugLog('[publicApi] silent login succeeded, retry status:', retried.status)
    return retried
  } catch (e) {
    // eslint-disable-next-line no-console
    debugLog('[publicApi] silent login failed:', e?.message)
    return null
  }
}

const publicApi = { get, post, patch, put, del, getBlob }

export default publicApi
export { BASE_URL, authHeaders, handleUnauthorized }
